/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2024 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import {
    ApiHandler,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
    markSkipOnCompleteIfOnErrorIsSet,
} from "@insite/client-framework/HandlerCreator";
import { ServiceResult } from "@insite/client-framework/Services/ApiService";
import { getShipmentPkgsFilesInfo, LoadShipmentPkgsFilesInfoApiParameter } from "@tps/App/Services/OrderDetailsService";
import { ShipmentPackageApiResultModel } from "@tps/App/Store/Reducers";

type HandlerType = ApiHandler<
    LoadShipmentPkgsFilesInfoApiParameter &
        HasOnSuccess<ServiceResult<ShipmentPackageApiResultModel>> &
        HasOnError<string>,
    ServiceResult<ShipmentPackageApiResultModel>
>;
export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = { ...props.parameter };
};

export const DispatchBeginLoadShipmentPkgsFilesInfo: HandlerType = props => {
    props.dispatch({
        type: "xOrderDetails/BeginLoadShipmentPackageInfo",
        key: props.apiParameter.orderNumber,
    } as any);
};

export const GetShipmentPkgsFilesInfo: HandlerType = async props => {
    const { onSuccess, onError, ...apiParameter } = props.apiParameter;
    props.apiResult = await getShipmentPkgsFilesInfo(apiParameter);
};

export const DispatchCompleteLoadShipmentPkgsFilesInfo: HandlerType = props => {
    props.dispatch({
        type: "xOrderDetails/CompleteLoadShipmentPackageInfo",
        data: (props.apiResult as any)?.result,
        key: props.apiParameter.orderNumber,
    } as any);
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (!props.apiResult.successful) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.(props.apiResult.errorMessage!);
    }
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (props.apiResult.successful) {
        props.parameter.onSuccess?.(props.apiResult);
    }
};

export const chain = [
    PopulateApiParameter,
    DispatchBeginLoadShipmentPkgsFilesInfo,
    GetShipmentPkgsFilesInfo,
    DispatchCompleteLoadShipmentPkgsFilesInfo,
    ExecuteOnErrorCallback,
    ExecuteOnSuccessCallback,
];
const loadShipmentPkgsFilesInfo = createHandlerChainRunner(chain, "LoadShipmentPkgsFilesInfo");

export default loadShipmentPkgsFilesInfo;
